import store from '@/store';
import { getPlatformInfo } from '@/utils/deviceUtils';
import _ from 'lodash';
import DDLogs from '@/DDLogs';
import Segment from '@/Segment';
import config from './config';

// True when show() was called from a user-initiated path. Consumed in onShow
// so init-time Intercom('show') calls in initIntercomEvents don't count as opens.
let userInitiatedShowPending = false;

// True once the messenger iframe's own scripts have actually executed and called
// back into the parent (onUnreadCountChange fires on init). window.Intercom.booted
// only means the iframe element + script tags were created — not that the scripts
// inside it ran. This flag is the real "iframe is alive" signal.
let iframeMessengerAlive = false;

let showRetryTimer = null;
let showRebootTimer = null;
let bootSyncPending = false;
let launcherSuppressed = false;
let clipRemovalPending = false;
let startupUnreadEnabled = false;
let launcherShown = false;
let lastBootArgs = null;
let lastUpdateArgs = null;
// The most recent open command to replay on retry/reboot. Updated on every
// _scheduleOpenRecovery call so a newer open (e.g. a different conversation)
// supersedes an in-flight recovery instead of reopening the original target.
let latestReopen = null;
const SHOW_RETRY_DELAY_MS = 1000;
const SHOW_REBOOT_DELAY_MS = 1000;
const BOOT_SYNC_FAILSAFE_MS = 5000;

/* eslint-disable */
export default {
  install(Vue, options) {
    includeIntercom();
  },

  async boot(customerId, householdName, extraAttributes) {
    const { intercomEnabled, appId, normalizedExtraAttributes } = _getIntercomAttributes(extraAttributes);
    if (intercomEnabled === "true") {
      if (!_isIntercomAvailable()) {
        DDLogs.error('[Intercom Debug] Intercom not available on window during boot');
        return;
      }
      DDLogs.info('[Intercom Debug] Booting Intercom', { customerId });
      lastBootArgs = {
        app_id: appId,
        user_id: customerId,
        name: householdName,
        hide_default_launcher: true,
        ...normalizedExtraAttributes,
      };
      window.Intercom('boot', lastBootArgs);

      this.initIntercomEvents();
      _hideLauncherIcon();
    }
  },
  // Open the messenger invisibly so the unread count syncs, then reveal the launcher
  // only if there's unread. Deferred to idle so it doesn't delay first paint.
  enableStartupUnreadLauncher() {
    if (startupUnreadEnabled) return;
    if (!_isIntercomAvailable() || !lastBootArgs) return;
    startupUnreadEnabled = true;
    const startBootSync = function() {
      if (!_isIntercomAvailable()) return; // runs later via idle/timeout — re-check
      bootSyncPending = true;
      _setBootSyncHidden(true);
      // Schedule cleanup before showing so the clip is always lifted even if show throws.
      setTimeout(function() { _finishBootSync(false); }, BOOT_SYNC_FAILSAFE_MS);
      // Plain show (not this.show()) so the boot-sync never runs the reboot recovery,
      // which can leave the messenger stuck open full-screen on mobile.
      window.Intercom('show');
      // Retry once, only if it hasn't opened yet (onShow sets isOpen) and still available.
      setTimeout(function() {
        if (bootSyncPending && !store.state.intercom.isOpen && _isIntercomAvailable()) {
          window.Intercom('show');
        }
      }, SHOW_RETRY_DELAY_MS);
      // Telemetry: log once whether the messenger actually painted on screen during the hold.
      let everVisible = false;
      [600, 1500, 2500, 3500, 4500].forEach(function(ms) {
        setTimeout(function() { if (_bootSyncMessengerVisible()) everVisible = true; }, ms);
      });
      setTimeout(function() {
        const info = getPlatformInfo();
        DDLogs.info('[Intercom] boot-sync visibility', {
          customerId: lastBootArgs && lastBootArgs.user_id,
          visible: everVisible,
          platform: info.platform,
          appVersion: info.appVersion,
        });
      }, 4800);
    };
    if (window.requestIdleCallback) {
      window.requestIdleCallback(startBootSync, { timeout: 4000 });
    } else {
      setTimeout(startBootSync, 2500);
    }
  },
  show() {
    if (_isIntercomAvailable()) {
      DDLogs.info('[Intercom Debug] show clicked and Intercom available on window', {
        iframeExists: document.getElementById('intercom-frame') !== null,
        widgetBooted: !!window.Intercom.booted,
        iframeAlive: iframeMessengerAlive,
        intercomIsOpen: !!store.state.intercom.isOpen,
      });
      userInitiatedShowPending = true;
      window.Intercom('show');
      this._scheduleOpenRecovery(function() {
        window.Intercom('show');
      });
    } else {
      DDLogs.error('[Intercom Debug] show attempted but Intercom not available on window');
    }
  },
  // Retry → reboot fallback when a user-initiated open doesn't produce onShow
  // (the queued open command is dropped before the iframe is alive). The timers
  // replay latestReopen — the most recent open command (show / showConversation) —
  // so the reboot reopens the current target, not the messenger home or a stale
  // conversation. Timers are cleared in onShow on success.
  _scheduleOpenRecovery(reopen) {
    latestReopen = reopen;
    if (showRetryTimer) return;
    const self = this;
    showRetryTimer = setTimeout(function() {
      showRetryTimer = null;
      if (!userInitiatedShowPending) return;
      DDLogs.info('[Intercom Debug] show retry fired', {
        iframeAlive: iframeMessengerAlive,
        widgetBooted: !!window.Intercom.booted,
      });
      latestReopen();
      if (showRebootTimer) return;
      showRebootTimer = setTimeout(function() {
        showRebootTimer = null;
        if (!userInitiatedShowPending || !lastBootArgs) return;
        const currentCustomerId = store.state.session && store.state.session.customerId;
        if (String(lastBootArgs.user_id) !== String(currentCustomerId)) {
          DDLogs.info('[Intercom Debug] reboot aborted — cached boot args are stale for current session', {
            cachedUserId: lastBootArgs.user_id,
            currentCustomerId,
          });
          return;
        }
        DDLogs.info('[Intercom Debug] show reboot triggered — retry also failed', {
          iframeAlive: iframeMessengerAlive,
        });
        iframeMessengerAlive = false;
        window.Intercom('shutdown');
        window.Intercom('boot', lastBootArgs);
        self.initIntercomEvents();
        if (lastUpdateArgs) {
          window.Intercom('update', lastUpdateArgs);
        }
        latestReopen();
      }, SHOW_REBOOT_DELAY_MS);
    }, SHOW_RETRY_DELAY_MS);
  },
  hide() {
    if (_isIntercomAvailable()) {
      window.Intercom('hide');
    } else {
      DDLogs.info('[Intercom Debug] hide attempted but Intercom not available');
    }
  },
  async update(customerId, householdName, extraAttributes) {
    const { intercomEnabled, appId, normalizedExtraAttributes } = _getIntercomAttributes(extraAttributes);
    if (intercomEnabled === "true") {
      if (!_isIntercomAvailable()) {
        DDLogs.error('[Intercom Debug] Intercom not available on window during update');
        return;
      }
      lastUpdateArgs = {
        app_id: appId,
        user_id: customerId,
        name: householdName,
        hide_default_launcher: !startupUnreadEnabled || launcherSuppressed || !(store.state.intercom.unreadMessages > 0),
        ...normalizedExtraAttributes,
      };
      window.Intercom('update', lastUpdateArgs);
    }
  },
  hideLauncher() {
    DDLogs.info('[Intercom Debug] hideLauncher called', { isAvailable: _isIntercomAvailable() });
    launcherSuppressed = true;
    window.Intercom('update', {
      'hide_default_launcher': true
    });
  },
  showLauncher() {
    DDLogs.info('[Intercom Debug] showLauncher called');
    launcherSuppressed = false;
    _applyLauncherVisibility();
  },
  showNewMessage(prompt = '') {
    if (_isIntercomAvailable()) {
      DDLogs.info('[Intercom Debug] showNewMessage called');
      userInitiatedShowPending = true;
      window.Intercom('showNewMessage', prompt);
      this._scheduleOpenRecovery(function() {
        window.Intercom('showNewMessage', prompt);
      });
    } else {
      DDLogs.info('[Intercom Debug] showNewMessage attempted but Intercom not available');
    }
  },
  showConversation(conversationId) {
    if (_isIntercomAvailable()) {
      DDLogs.info('[Intercom Debug] showConversation called', { conversationId });
      userInitiatedShowPending = true;
      window.Intercom('showConversation', conversationId);
      this._scheduleOpenRecovery(function() {
        window.Intercom('showConversation', conversationId);
      });
    } else {
      DDLogs.info('[Intercom Debug] showConversation attempted but Intercom not available');
    }
  },
  initIntercomEvents() {
    if (!window.Intercom) {
      DDLogs.info('[Intercom Debug] initIntercomEvents skipped — Intercom not on window');
      return;
    }
    DDLogs.info('[Intercom Debug] initIntercomEvents started');

    const { platform, appVersion } = getPlatformInfo();
    window.Intercom('onShow', function() {
      if (userInitiatedShowPending) {
        userInitiatedShowPending = false;
        if (showRetryTimer) {
          clearTimeout(showRetryTimer);
          showRetryTimer = null;
        }
        if (showRebootTimer) {
          clearTimeout(showRebootTimer);
          showRebootTimer = null;
        }
        if (!bootSyncPending) {
          DDLogs.info('[Intercom Debug] onShow fired — messenger actually opened');
          Segment.trackUserInteraction('intercom_chat_opened');
        }
      }
      window.Intercom('update', { user_platform: platform, appVersion: appVersion });
      store.commit('intercom/setIsOpen', true);

      // Count synced; bootSyncPending stays true here so the open-tracking above stays
      // suppressed. _finishBootSync flips it.
      if (bootSyncPending) {
        setTimeout(function() { _finishBootSync(true); }, 1000);
      }
    });

    window.Intercom('onHide', function() {
      store.commit('intercom/setIsOpen', false);
      if (clipRemovalPending) {
        clipRemovalPending = false;
        _setBootSyncHidden(false);
        return;
      }
      Segment.trackUserInteraction('intercom_chat_closed');
    });

    if (store.state.intercom.showOnInit) {
      window.Intercom('show');
    }

    window.Intercom('onUnreadCountChange', (unreadCount) => {
      iframeMessengerAlive = true;
      store.commit('intercom/setUnreadMessages', unreadCount);
      if (startupUnreadEnabled && !bootSyncPending) {
        _applyLauncherVisibility();
      }
    });
  }
};
// Close the invisible panel, set the launcher, lift the hide. `opened` (onShow path)
// defers the clip removal to onHide so the closing panel is never exposed; the failsafe
// path never opened, so lift directly.
function _finishBootSync(opened) {
  if (!bootSyncPending) return;
  bootSyncPending = false;
  userInitiatedShowPending = false;
  if (showRetryTimer) { clearTimeout(showRetryTimer); showRetryTimer = null; }
  if (showRebootTimer) { clearTimeout(showRebootTimer); showRebootTimer = null; }
  _applyLauncherVisibility();
  if (opened) {
    clipRemovalPending = true;
    window.Intercom('hide');
    setTimeout(function() {
      if (clipRemovalPending) {
        clipRemovalPending = false;
        _setBootSyncHidden(false);
      }
    }, 2500);
  } else {
    window.Intercom('hide');
    _setBootSyncHidden(false);
  }
}

// Keep the launcher enabled (so Intercom still surfaces away-message banners on open)
// but hide the blue icon + its unread badge. The frame fallback is a separate rule from
// the :has() wrapper rule so it still applies where :has is unsupported (a combined list
// would drop entirely). Never touches the notifications banner.
function _hideLauncherIcon() {
  const STYLE_ID = 'intercom-hide-launcher';
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent =
    '.intercom-lightweight-app-launcher,'
    + 'iframe[name="intercom-launcher-frame"]{display:none !important;}'
    + ':has(> iframe[name="intercom-launcher-frame"]){display:none !important;}';
  document.head.appendChild(style);
}

function _applyLauncherVisibility() {
  if (!_isIntercomAvailable() || !lastBootArgs) return;
  const shown = startupUnreadEnabled && !launcherSuppressed && store.state.intercom.unreadMessages > 0;
  if (shown && !launcherShown) {
    const { platform, appVersion } = getPlatformInfo();
    DDLogs.info('[Intercom] startup unread launcher shown', {
      customerId: lastBootArgs.user_id,
      unreadCount: store.state.intercom.unreadMessages,
      platform,
      appVersion,
    });
  }
  launcherShown = shown;
  window.Intercom('update', { hide_default_launcher: !shown });
}

// Clip everything Intercom injects (clip-path: it never sets that inline, so our rule
// wins while the iframe stays alive to sync the count). MUST exclude html/body: on
// mobile, opening adds "intercom-mobile-messenger-active" to <html>, which the
// substring match would otherwise catch — clipping the whole document white.
// Intercom's messenger painted over screen-center? (clip sets pointer-events:none, so
// elementFromPoint hits the app when hidden, an Intercom element when visible.)
function _bootSyncMessengerVisible() {
  let node = document.elementFromPoint(window.innerWidth / 2, window.innerHeight / 2);
  for (let i = 0; i < 4 && node; i++) {
    const id = node.id || '';
    const cls = typeof node.className === 'string' ? node.className : '';
    const name = (node.getAttribute && node.getAttribute('name')) || '';
    if (/intercom/i.test(id) || /intercom/i.test(cls) || /intercom/i.test(name)) return true;
    node = node.parentElement;
  }
  return false;
}

function _setBootSyncHidden(hidden) {
  const STYLE_ID = 'intercom-boot-sync-hide';
  const existing = document.getElementById(STYLE_ID);
  if (hidden) {
    if (existing) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent =
      '[class*="intercom-"]:not(.intercom-button):not(html):not(body),'
      + '[id*="intercom"]:not(html):not(body),'
      + 'iframe[name*="intercom"],'
      + 'iframe[title*="Intercom" i]{'
      + 'clip-path:inset(100%) !important;'
      + '-webkit-clip-path:inset(100%) !important;'
      + 'opacity:0 !important;'
      + 'pointer-events:none !important;}';
    document.head.appendChild(style);
  } else if (existing) {
    existing.remove();
  }
}

function includeIntercom() {
  const w = window;
  const ic = w.Intercom;
  if (typeof ic === 'function') {
    ic('reattach_activator');
    ic('update', w.intercomSettings);
  } else {
    const d = document;
    const i = function() {
      i.c(arguments);
    };
    i.q = [];
    i.c = function(args) {
      i.q.push(args);
    };
    w.Intercom = i;
    const l = function() {
      const s = d.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.src = 'https://widget.intercom.io/widget/APP_ID';
      const x = d.getElementsByTagName('script')[0];
      x.parentNode.insertBefore(s, x);
    };
    // includeIntercom runs at Vue plugin install, when readyState is usually
    // 'interactive' — waiting for the 'load' event injects the script too late
    // (or never, if the session ends first), so run as soon as the DOM is ready.
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      l();
    } else {
      w.addEventListener('DOMContentLoaded', l, false);
    }
  }
}

function _isIntercomAvailable() {
  return window.Intercom && typeof window.Intercom === 'function';
}

function _getIntercomAttributes(extraAttributes) {
  const { intercomEnabled, intercomProdAppId } = config.get();
  const normalizedExtraAttributes = _.mapKeys(extraAttributes, (v, k) => _.snakeCase(k));
  return { intercomEnabled, appId: intercomProdAppId, normalizedExtraAttributes };
}
